import React, { useEffect, useState, useCallback } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import Routes from './Routes'
import { setAnalyticsUser } from './utils/analytics'
import Toast from './components/Loadboard/Searches/Toast'

import { DarkModeProvider } from './context/DarkModeContext'
import { SubscriptionProvider } from './context/SubscriptionContext'
import { SidebarProvider } from './context/SidebarContext'
import { getCurrentUser } from './services/authService'
import AppInstallBanner from './components/AppInstallBanner'

import TelegramGroupConnect from './components/TelegramGroupConnect/TelegramGroupConnect'
import TeamInvitationModal from './components/TeamInvitationModal'
import { startSession } from './services/sessionCookie'

const queryClient = new QueryClient()

function App() {
	const [user, setUser] = useState(null)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [urlData, setUrlData] = useState(null)
	const [toastShow, setToastShow] = useState(false)
	const [toastMessage, setToastMessage] = useState('')

	const queryString = window.location.search
	const urlParams = new URLSearchParams(queryString)
	const token = urlParams.get('token')
	const chatId = urlParams.get('chatId')

	const refreshToken = useCallback(async () => {
		const tokenRaw = localStorage.getItem('jwtToken')
		if (!tokenRaw) return

		try {
			const decodedToken = jwtDecode(tokenRaw)
			const currentTime = Date.now() / 1000
			const tokenAge = currentTime - decodedToken.iat
			const threeHoursInSeconds = 3 * 60 * 60
			const subscription = decodedToken?.subscriptionType

			if (decodedToken?.deviceLimitReached) return

			if (!subscription || tokenAge >= threeHoursInSeconds) {
				const response = await axios.post('/api/user/refresh-token/web')
				const newToken = response.data.token

				if (response.data.success) {
					startSession(newToken)
				} else {
					console.log('Failed to refresh token')
				}
			}
		} catch (error) {
			console.error('Error in token refresh process:', error)
		}
	}, [])

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (user) refreshToken()
		}, 2 * 60 * 1000)

		return () => clearInterval(intervalId)
	}, [user, refreshToken])

	useEffect(() => {
		const fetchUserAndHandleTelegramLink = async () => {
			if (token && chatId) {
				try {
					const url = '/api/user/link-telegram-group'

					const response = await axios.get(url, { params: { token, chatId } })

					console.log('response', response?.data?.groupName)

					setIsModalOpen(true)
					setUrlData({ token, chatId, groupName: response.data.groupName })
				} catch (error) {
					const user = getCurrentUser()

					console.log('Error linking Telegram group:', error)

					if (user) {
						let errorMessage = 'Error linking Telegram group'
						if (error?.response?.data?.message) {
							errorMessage = error.response.data.message
						}

						setToastShow(true)
						setToastMessage(errorMessage)

						setTimeout(() => {
							setToastShow(false)
						}, 5000)
					}
				}
			}
		}

		fetchUserAndHandleTelegramLink()
	}, [])

	useEffect(() => {
		const fetchUser = async () => {
			const user = getCurrentUser()

			if (user) {
				refreshToken()
				setUser(user)
				setAnalyticsUser(user.id, user.email)
				window.clarity('identify', user?.email)

				if (window.$crisp) {
					window.$crisp.push(['set', 'user:email', user?.email])
					window.$crisp.push(['set', 'user:nickname', user?.email])
				}
			}
		}

		fetchUser()
	}, [])

	return (
		<QueryClientProvider client={queryClient}>
			<DarkModeProvider>
				<SubscriptionProvider>
					<SidebarProvider>
						<BrowserRouter>
							<ToastContainer />

							{/* Shown signed out too: someone at the login screen is a
							    sign-up away, and the app is part of the pitch. */}
							<AppInstallBanner />

							<Routes user={user} />

							<TelegramGroupConnect
								isModalOpen={isModalOpen}
								closeModal={() => setIsModalOpen(false)}
								urlData={urlData}
							/>

							{user && <TeamInvitationModal />}

							{toastShow && <Toast message={toastMessage} error={true} />}
						</BrowserRouter>
					</SidebarProvider>
				</SubscriptionProvider>
			</DarkModeProvider>
		</QueryClientProvider>
	)
}

export default App
